import { useState, useEffect, useMemo } from "react";
import {
  PlusCircle,
  ArrowUpRight,
  Wallet,
  TrendingUp,
  TrendingDown,
  Search,
  Filter,
  Eye,
  X,
  AlertTriangle,
  DollarSign,
} from "lucide-react";
import "./DepositosDashboard.scss";

import { useHttpClient } from "../../../shared/hooks/http-hook";
import { useLoading } from "../../../shared/context/LoadingContext";
import {
  ConfirmacaoCancelamentoModal,
  DetalhesDepositoModal,
} from "./components/DetalhesDepositoModal";

import toast from "react-hot-toast";
import FiltrosDepositosModal from "./components/FiltrosDepositosModal";

import DatePickerPT from "./components/DatePickerPt";

/* A caixa do dinheiro físico. Tolerante a acentos, maiúsculas e espaços:
   "Clínica", "clinica" e " CLINICA " são a mesma caixa. */
const ehCaixaClinica = (nome) =>
  String(nome || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase() === "clinica";

const DepositosDashboard = ({ clinicaId, auth }) => {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();

  const [saldos, setSaldos] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [showNovoDeposito, setShowNovoDeposito] = useState(false);
  const [showSaldoInicial, setShowSaldoInicial] = useState(false);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);

  const [showFiltros, setShowFiltros] = useState(false);
  const [filtros, setFiltros] = useState({
    dataInicio: "",
    dataFim: "",
    caixa: "",
    status: "",
    valorMin: "",
    valorMax: "",
    busca: "",
  });

  const [caixas, setCaixas] = useState([]);
  const [saldosIniciais, setSaldosIniciais] = useState([]);

  // ESTADOS para as modais:
  const [showDetalhes, setShowDetalhes] = useState(null);
  const [confirmCancelamento, setConfirmCancelamento] = useState({
    open: false,
    deposito: null,
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtros]);

  const fetchData = async () => {
    startLoading();
    const [
      depositosResponse,
      saldosResponse,
      caixasResponse,
      saldosIniciaisResponse,
    ] = await Promise.all([
      sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/depositoscaixa/clinica/${clinicaId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      ),
      sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/depositoscaixa/saldo/clinica/${clinicaId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      ),
      sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinicaId}?todas=true`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      ),
      sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/saldosiniciais/clinica/${clinicaId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      ),
    ]);
    stopLoading();

    setSaldosIniciais(saldosIniciaisResponse.saldosIniciais);
    setCaixas(caixasResponse.caixas);
    setSaldos(saldosResponse.saldos);
    setDepositos(depositosResponse.depositos);
  };

  const totalGeral = saldos.reduce((acc, item) => acc + item.saldo, 0);
  const saldoClinica =
    saldos.find((s) => ehCaixaClinica(s.caixa.caixa))?.saldo || 0;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "CVE",
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);

    // Se a data vier como string YYYY-MM-DD, tratar localmente
    if (typeof date === "string" && !date.includes("T")) {
      const [year, month, day] = date.split("-");
      return new Date(year, month - 1, day).toLocaleDateString("pt-PT");
    }

    // Para datas com horário, ajustar fuso
    const adjustedDate = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString("pt-PT"); // dd/mm/yyyy
  };

  const formatarDataParaTexto = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  };

  const handleVerDetalhes = (deposito) => {
    setShowDetalhes(deposito);
  };

  const handleCancelarDeposito = (deposito) => {
    setConfirmCancelamento({ open: true, deposito });
  };

  const handleConfirmarCancelamento = async (depositoId, motivo) => {
    startLoading();

    const formData = new FormData();
    formData.append("utilizador", auth.userId);
    formData.append("motivoCancelamento", motivo);
    try {
      // Substituir pela sua função de API
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/depositoscaixa/cancelar/${depositoId}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      // Recarregar dados
      fetchData(); // ← Sua função de recarregar
      setConfirmCancelamento({ open: false, deposito: null });
      toast.success("Depósito cancelado com sucesso!");
    } catch (error) {
      console.error("Erro ao cancelar depósito:", error);
    } finally {
      stopLoading();
    }
  };

  const depositosFiltrados = useMemo(() => {
    let resultado = [...depositos];

    // Filtro de busca por texto
    if (filtros.busca) {
      const termo = filtros.busca.toLowerCase();
      resultado = resultado.filter(
        (deposito) =>
          deposito.caixaOrigem.caixa.toLowerCase().includes(termo) ||
          deposito.caixaDestino.caixa.toLowerCase().includes(termo) ||
          deposito.observacoes?.toLowerCase().includes(termo) ||
          deposito.criadoPor.name.toLowerCase().includes(termo) ||
          deposito.valor.toString().includes(termo),
      );
    }

    // Filtro de data início
    if (filtros.dataInicio) {
      const dataInicio = new Date(filtros.dataInicio);
      resultado = resultado.filter(
        (deposito) => new Date(deposito.dataDeposito) >= dataInicio,
      );
    }

    // Filtro de data fim
    if (filtros.dataFim) {
      const dataFim = new Date(filtros.dataFim);
      dataFim.setHours(23, 59, 59, 999); // Incluir todo o dia
      resultado = resultado.filter(
        (deposito) => new Date(deposito.dataDeposito) <= dataFim,
      );
    }

    // Filtro por caixa (origem ou destino)
    if (filtros.caixa) {
      resultado = resultado.filter(
        (deposito) =>
          deposito.caixaOrigem._id === filtros.caixa ||
          deposito.caixaDestino._id === filtros.caixa,
      );
    }

    // Filtro por status
    if (filtros.status) {
      if (filtros.status === "ativo") {
        resultado = resultado.filter((deposito) => !deposito.cancelado);
      } else if (filtros.status === "cancelado") {
        resultado = resultado.filter((deposito) => deposito.cancelado);
      }
    }

    // Filtro por valor mínimo
    if (filtros.valorMin) {
      const valorMin = parseFloat(filtros.valorMin);
      resultado = resultado.filter((deposito) => deposito.valor >= valorMin);
    }

    // Filtro por valor máximo
    if (filtros.valorMax) {
      const valorMax = parseFloat(filtros.valorMax);
      resultado = resultado.filter((deposito) => deposito.valor <= valorMax);
    }

    return resultado;
  }, [depositos, filtros]);

  const dadosPaginados = useMemo(() => {
    const indexInicio = (paginaAtual - 1) * itensPorPagina;
    const indexFim = indexInicio + itensPorPagina;
    return depositosFiltrados.slice(indexInicio, indexFim);
  }, [depositosFiltrados, paginaAtual, itensPorPagina]);

  const totalPaginas = Math.ceil(depositosFiltrados.length / itensPorPagina);

  // Função para limpar todos os filtros
  const handleLimparFiltros = () => {
    setFiltros({
      dataInicio: "",
      dataFim: "",
      caixa: "",
      status: "",
      valorMin: "",
      valorMax: "",
      busca: "",
    });
    setPaginaAtual(1); // ← Adicionar esta linha
  };

  // Contar filtros ativos (para mostrar badge)
  const contarFiltrosAtivos = () => {
    return Object.entries(filtros).filter(
      ([key, value]) => key !== "busca" && value !== "",
    ).length;
  };

  return (
    <div className="depositoCaixa-container">
      {/* Header */}
      <div className="depositoCaixa-header">
        <div className="depositoCaixa-header-content">
          <div className="depositoCaixa-header-info">
            <h1 className="depositoCaixa-title">Gestão de Depósitos</h1>
            <p className="depositoCaixa-subtitle">
              Controle os saldos e movimentações entre caixas
            </p>
          </div>
          <div className="depositoCaixa-header-actions">
            <button
              onClick={() => setShowSaldoInicial(true)}
              className="depositoCaixa-btn depositoCaixa-btn-yellow"
            >
              <Wallet className="depositoCaixa-icon" />
              Saldo Inicial
            </button>
            <button
              onClick={() => setShowNovoDeposito(true)}
              className="depositoCaixa-btn depositoCaixa-btn-primary"
            >
              <PlusCircle className="depositoCaixa-icon" />
              Novo Depósito
            </button>
          </div>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="depositoCaixa-summary-cards">
        <div className="depositoCaixa-card">
          <div className="depositoCaixa-card-content">
            <div className="depositoCaixa-card-info">
              <p className="depositoCaixa-card-label">Total em Caixas</p>
              <p className="depositoCaixa-card-value">
                {formatCurrency(totalGeral)}
              </p>
            </div>
            <div className="depositoCaixa-card-icon depositoCaixa-card-icon-blue">
              <DollarSign className="depositoCaixa-icon-lg" />
            </div>
          </div>
        </div>

        <div className="depositoCaixa-card">
          <div className="depositoCaixa-card-content">
            <div className="depositoCaixa-card-info">
              <p className="depositoCaixa-card-label">Dinheiro na Clínica</p>
              <p className="depositoCaixa-card-value">
                {formatCurrency(saldoClinica)}
              </p>
            </div>
            <div className="depositoCaixa-card-icon depositoCaixa-card-icon-green">
              <Wallet className="depositoCaixa-icon-lg" />
            </div>
          </div>
        </div>

        <div className="depositoCaixa-card">
          <div className="depositoCaixa-card-content">
            <div className="depositoCaixa-card-info">
              <p className="depositoCaixa-card-label">Depósitos Este Mês</p>
              <p className="depositoCaixa-card-value">
                {contarFiltrosAtivos() > 0
                  ? depositosFiltrados.length
                  : depositos.length}
              </p>
            </div>
            <div className="depositoCaixa-card-icon depositoCaixa-card-icon-purple">
              <ArrowUpRight className="depositoCaixa-icon-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Saldos por Caixa */}
      <div className="depositoCaixa-section">
        <div className="depositoCaixa-section-header">
          <h2 className="depositoCaixa-section-title">Saldos por Caixa</h2>
        </div>
        <div className="depositoCaixa-section-content">
          <div className="depositoCaixa-saldo-list">
            {saldos.map((item) => (
              <div key={item.caixa._id} className="depositoCaixa-saldo-item">
                <div className="depositoCaixa-saldo-info">
                  <div
                    className={`depositoCaixa-saldo-icon ${
                      ehCaixaClinica(item.caixa.caixa)
                        ? "depositoCaixa-saldo-icon-green"
                        : "depositoCaixa-saldo-icon-blue"
                    }`}
                  >
                    <Wallet className="depositoCaixa-icon" />
                  </div>
                  <div className="depositoCaixa-saldo-details">
                    <p className="depositoCaixa-saldo-name">
                      {item.caixa.caixa}
                    </p>
                    <p className="depositoCaixa-saldo-initial">
                      {item.semSaldoInicial ? (
                        "Saldo inicial por definir"
                      ) : (
                        <>
                          Saldo inicial:{" "}
                          {formatCurrency(item.detalhes.saldoInicial)}
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="depositoCaixa-saldo-values">
                  <p className="depositoCaixa-saldo-current">
                    {item.semSaldoInicial ? "—" : formatCurrency(item.saldo)}
                  </p>
                  <p className="depositoCaixa-saldo-variation">
                    {item.semSaldoInicial ? (
                      <span className="depositoCaixa-variation-neutral">
                        Sem cálculo
                      </span>
                    ) : item.saldo > item.detalhes.saldoInicial ? (
                      <span className="depositoCaixa-variation-positive">
                        <TrendingUp className="depositoCaixa-icon-sm" />+
                        {formatCurrency(
                          item.saldo - item.detalhes.saldoInicial,
                        )}
                      </span>
                    ) : item.saldo < item.detalhes.saldoInicial ? (
                      <span className="depositoCaixa-variation-negative">
                        <TrendingDown className="depositoCaixa-icon-sm" />
                        {formatCurrency(
                          item.saldo - item.detalhes.saldoInicial,
                        )}
                      </span>
                    ) : (
                      <span className="depositoCaixa-variation-neutral">
                        Sem alteração
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Histórico de Depósitos */}
      <div className="depositoCaixa-section">
        <div className="depositoCaixa-section-header">
          <div className="depositoCaixa-history-header">
            <h2 className="depositoCaixa-section-title">
              Histórico de Depósitos
            </h2>
            <div className="depositoCaixa-history-actions">
              <div className="depositoCaixa-search-container">
                <Search className="depositoCaixa-search-icon" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="depositoCaixa-search-input"
                  value={filtros.busca}
                  onChange={(e) =>
                    setFiltros({ ...filtros, busca: e.target.value })
                  }
                />
              </div>
              <button
                onClick={() => setShowFiltros(true)}
                className={`depositoCaixa-btn depositoCaixa-btn-outline ${
                  contarFiltrosAtivos() > 0
                    ? "depositoCaixa-btn-with-badge"
                    : ""
                }`}
              >
                <Filter className="depositoCaixa-icon" />
                Filtros
                {contarFiltrosAtivos() > 0 && (
                  <span className="depositoCaixa-filter-badge">
                    {contarFiltrosAtivos()}
                  </span>
                )}
              </button>
              {contarFiltrosAtivos() > 0 && (
                <button
                  onClick={handleLimparFiltros}
                  className="depositoCaixa-btn depositoCaixa-btn-outline"
                  title="Limpar filtros"
                >
                  <X className="depositoCaixa-icon" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Indicador de filtros ativos */}
        {contarFiltrosAtivos() > 0 && (
          <div className="depositoCaixa-active-filters">
            <p className="depositoCaixa-active-filters-text">
              Mostrando {depositosFiltrados.length} de {depositos.length}{" "}
              depósitos
              {filtros.dataInicio &&
                ` • A partir de ${formatarDataParaTexto(filtros.dataInicio)}`}
              {filtros.dataFim &&
                ` • Até ${formatarDataParaTexto(filtros.dataFim)}`}
              {filtros.caixa &&
                ` • Caixa: ${
                  saldos.find((s) => s.caixa._id === filtros.caixa)?.caixa.caixa
                }`}
              {filtros.status &&
                ` • Status: ${
                  filtros.status === "ativo" ? "Ativos" : "Cancelados"
                }`}
              {filtros.valorMin &&
                ` • Valor min: ${formatCurrency(filtros.valorMin)}`}
              {filtros.valorMax &&
                ` • Valor max: ${formatCurrency(filtros.valorMax)}`}
            </p>
          </div>
        )}

        <div className="depositoCaixa-table-container">
          <table className="depositoCaixa-table">
            <thead className="depositoCaixa-table-header">
              <tr>
                <th className="depositoCaixa-table-th">Data</th>
                <th className="depositoCaixa-table-th">Origem</th>
                <th className="depositoCaixa-table-th">Destino</th>
                <th className="depositoCaixa-table-th">Valor</th>
                <th className="depositoCaixa-table-th">Observações</th>
                <th className="depositoCaixa-table-th">Criado por</th>
                <th className="depositoCaixa-table-th">Ações</th>
              </tr>
            </thead>
            <tbody className="depositoCaixa-table-body">
              {dadosPaginados.length === 0 ? (
                <tr>
                  <td colSpan="8" className="depositoCaixa-empty-state">
                    {depositos.length === 0
                      ? "Nenhum depósito encontrado"
                      : "Nenhum depósito corresponde aos filtros aplicados"}
                  </td>
                </tr>
              ) : (
                dadosPaginados.map((deposito) => (
                  <tr
                    key={deposito._id}
                    className={`depositoCaixa-table-row ${
                      deposito.cancelado
                        ? "depositoCaixa-table-row__cancelado"
                        : ""
                    }`}
                  >
                    <td className="depositoCaixa-table-td">
                      {formatDate(deposito.dataDeposito)}
                    </td>
                    <td className="depositoCaixa-table-td">
                      <span className="depositoCaixa-badge depositoCaixa-badge-red">
                        {deposito.caixaOrigem.caixa}
                      </span>
                    </td>
                    <td className="depositoCaixa-table-td">
                      <span className="depositoCaixa-badge depositoCaixa-badge-green">
                        {deposito.caixaDestino.caixa}
                      </span>
                    </td>
                    <td className="depositoCaixa-table-td depositoCaixa-table-value">
                      {formatCurrency(deposito.valor)}
                    </td>
                    <td className="depositoCaixa-table-td depositoCaixa-table-secondary">
                      {deposito.observacoes || "-"}
                    </td>
                    <td className="depositoCaixa-table-td depositoCaixa-table-secondary">
                      {deposito.criadoPor.name}
                    </td>
                    <td className="depositoCaixa-table-td">
                      <div className="depositoCaixa-table-actions">
                        <button
                          onClick={() => handleVerDetalhes(deposito)}
                          className="depositoCaixa-action-btn depositoCaixa-action-btn-blue"
                        >
                          <Eye className="depositoCaixa-icon-sm" />
                        </button>
                        {!deposito.cancelado && (
                          <button
                            onClick={() => handleCancelarDeposito(deposito)}
                            className="depositoCaixa-action-btn depositoCaixa-action-btn-red"
                          >
                            <X className="depositoCaixa-icon-sm" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {depositosFiltrados.length > 0 && (
            <div className="depositoCaixa-pagination">
              <div className="depositoCaixa-pagination-info">
                Mostrando {(paginaAtual - 1) * itensPorPagina + 1} a{" "}
                {Math.min(
                  paginaAtual * itensPorPagina,
                  depositosFiltrados.length,
                )}{" "}
                de {depositosFiltrados.length} registros
              </div>

              <div className="depositoCaixa-pagination-controls">
                <select
                  value={itensPorPagina}
                  onChange={(e) => {
                    setItensPorPagina(Number(e.target.value));
                    setPaginaAtual(1);
                  }}
                  className="depositoCaixa-pagination-select"
                >
                  <option value={10}>10 por página</option>
                  <option value={25}>25 por página</option>
                  <option value={50}>50 por página</option>
                  <option value={100}>100 por página</option>
                </select>

                <div className="depositoCaixa-pagination-buttons">
                  <button
                    onClick={() => setPaginaAtual(1)}
                    disabled={paginaAtual === 1}
                    className="depositoCaixa-pagination-btn"
                  >
                    Primeira
                  </button>
                  <button
                    onClick={() => setPaginaAtual(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                    className="depositoCaixa-pagination-btn"
                  >
                    Anterior
                  </button>
                  <span className="depositoCaixa-pagination-current">
                    Página {paginaAtual} de {totalPaginas}
                  </span>
                  <button
                    onClick={() => setPaginaAtual(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                    className="depositoCaixa-pagination-btn"
                  >
                    Próxima
                  </button>
                  <button
                    onClick={() => setPaginaAtual(totalPaginas)}
                    disabled={paginaAtual === totalPaginas}
                    className="depositoCaixa-pagination-btn"
                  >
                    Última
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Filtros */}
      <FiltrosDepositosModal
        open={showFiltros}
        onClose={() => setShowFiltros(false)}
        filtros={filtros}
        setFiltros={setFiltros}
        caixas={saldos.map((s) => s.caixa)}
        onLimparFiltros={handleLimparFiltros}
      />

      {/* Modal de Confirmação */}
      <ConfirmacaoCancelamentoModal
        open={confirmCancelamento.open}
        deposito={confirmCancelamento.deposito}
        onClose={() => setConfirmCancelamento({ open: false, deposito: null })}
        onConfirm={handleConfirmarCancelamento}
        startLoading={startLoading}
        stopLoading={stopLoading}
      />

      {/* Modal de Detalhes */}
      {showDetalhes && (
        <DetalhesDepositoModal
          deposito={showDetalhes}
          onClose={() => setShowDetalhes(null)}
        />
      )}

      {/* Modais */}
      {showNovoDeposito && (
        <NovoDepositoModal
          onClose={() => setShowNovoDeposito(false)}
          saldos={saldos}
          onSuccess={() => {
            setShowNovoDeposito(false);
            fetchData();
          }}
          caixas={caixas}
          sendRequest={sendRequest}
          startLoading={startLoading}
          stopLoading={stopLoading}
          auth={auth}
          clinicaId={clinicaId}
        />
      )}

      {showSaldoInicial && (
        <SaldoInicialModal
          onClose={() => setShowSaldoInicial(false)}
          onSuccess={() => {
            setShowSaldoInicial(false);
            fetchData();
          }}
          saldosIniciais={saldosIniciais}
          caixas={caixas}
          sendRequest={sendRequest}
          startLoading={startLoading}
          stopLoading={stopLoading}
          auth={auth}
          clinicaId={clinicaId}
        />
      )}
    </div>
  );
};

// Modal para Novo Depósito
const NovoDepositoModal = ({
  onClose,
  saldos,
  onSuccess,
  caixas,
  sendRequest,
  startLoading,
  stopLoading,
  auth,
  clinicaId,
}) => {
  const [formData, setFormData] = useState({
    caixaOrigemId: "", // ← Começar vazio
    caixaDestinoId: "", // ← Começar vazio
    valor: "",
    dataDeposito: new Date().toISOString().split("T")[0],
    observacoes: "",
  });

  useEffect(() => {
    const clinicaCaixa = caixas.find((c) => ehCaixaClinica(c.caixa));
    if (clinicaCaixa) {
      setFormData((prev) => ({
        ...prev,
        caixaOrigemId: clinicaCaixa._id,
      }));
    }
  }, [caixas]);

  const [error, setError] = useState("");

  const caixaOrigem = saldos.find(
    (s) => s.caixa._id === formData.caixaOrigemId,
  );

  const saldoDisponivel = caixaOrigem?.saldo || 0;

  const handleSubmit = async () => {
    setError("");

    const hoje = new Date();
    hoje.setHours(23, 59, 59, 999); // Final do dia de hoje

    const dataEscolhida = new Date(formData.dataDeposito);

    /* As validações correm ANTES de ligar o carregamento: saíam com o
       carregamento ligado, e o ecrã ficava bloqueado por trás do erro. */
    if (dataEscolhida > hoje) {
      setError("Não é possível fazer depósitos para datas futuras");
      return;
    }

    if (caixaOrigem?.semSaldoInicial) {
      setError(
        "A caixa de origem não tem saldo inicial definido. Defina-o antes de fazer depósitos.",
      );
      return;
    }

    if (parseFloat(formData.valor) > saldoDisponivel) {
      setError(
        `Saldo insuficiente. Disponível: CVE${saldoDisponivel.toFixed(2)}`,
      );
      return;
    }

    startLoading();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("clinicaId", clinicaId);
      formDataToSend.append("caixaDestinoId", formData.caixaDestinoId);
      formDataToSend.append("caixaOrigemId", formData.caixaOrigemId);
      formDataToSend.append("valor", formData.valor);
      formDataToSend.append("dataDeposito", formData.dataDeposito);
      formDataToSend.append("observacoes", formData.observacoes || "");
      formDataToSend.append("criadoPor", auth.userId);

      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/depositoscaixa/`,
        "POST",
        formDataToSend,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      onSuccess();
    } catch (err) {
      setError("Erro ao criar depósito");
    } finally {
      stopLoading();
    }
  };

  return (
    <div className="depositoCaixa-modal-overlay">
      <div className="depositoCaixa-modal">
        <div className="depositoCaixa-modal-header">
          <div className="depositoCaixa-modal-header-content">
            <h2 className="depositoCaixa-modal-title">Novo Depósito</h2>
            <button onClick={onClose} className="depositoCaixa-modal-close">
              <X className="depositoCaixa-icon" />
            </button>
          </div>
        </div>

        <div className="depositoCaixa-modal-content">
          {error && (
            <div className="depositoCaixa-alert depositoCaixa-alert-error">
              <AlertTriangle className="depositoCaixa-icon" />
              <span className="depositoCaixa-alert-text">{error}</span>
            </div>
          )}

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">Caixa de Origem</label>
            <select
              value={formData.caixaOrigemId}
              onChange={(e) =>
                setFormData({ ...formData, caixaOrigemId: e.target.value })
              }
              className="depositoCaixa-select"
              required
            >
              <option value="">Selecione...</option>
              {saldos.map((saldo) => (
                <option key={saldo.caixa._id} value={saldo.caixa._id}>
                  {saldo.caixa.caixa}{" "}
                  {saldo.semSaldoInicial
                    ? "(sem saldo inicial)"
                    : `(CVE${saldo.saldo.toFixed(2)})`}
                </option>
              ))}
            </select>
          </div>

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">Caixa de Destino</label>
            <select
              value={formData.caixaDestinoId}
              onChange={(e) =>
                setFormData({ ...formData, caixaDestinoId: e.target.value })
              }
              className="depositoCaixa-select"
              required
            >
              <option value="">Selecione...</option>
              {saldos
                .filter((s) => s.caixa._id !== formData.caixaOrigemId)
                .map((saldo) => (
                  <option key={saldo.caixa._id} value={saldo.caixa._id}>
                    {saldo.caixa.caixa}
                  </option>
                ))}
            </select>
          </div>

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">Valor</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={saldoDisponivel}
              value={formData.valor}
              onChange={(e) =>
                setFormData({ ...formData, valor: e.target.value })
              }
              className="depositoCaixa-input"
              placeholder="0.00"
              required
            />
            {caixaOrigem && (
              <p className="depositoCaixa-help-text">
                {caixaOrigem.semSaldoInicial
                  ? "Esta caixa não tem saldo inicial definido."
                  : `Saldo disponível: CVE${saldoDisponivel.toFixed(2)}`}
              </p>
            )}
          </div>

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">Data do Depósito</label>
            <DatePickerPT
              value={formData.dataDeposito}
              onChange={(value) =>
                setFormData({ ...formData, dataDeposito: value })
              }
              maxDate={new Date().toISOString().split("T")[0]}
              placeholder="Selecione a data do depósito"
              required
            />
          </div>

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">Observações</label>
            <textarea
              value={formData.observacoes}
              onChange={(e) =>
                setFormData({ ...formData, observacoes: e.target.value })
              }
              className="depositoCaixa-textarea"
              rows="3"
              placeholder="Observações opcionais..."
            />
          </div>

          <div className="depositoCaixa-modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="depositoCaixa-btn depositoCaixa-btn-outline"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="depositoCaixa-btn depositoCaixa-btn-primary"
            >
              Criar Depósito
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal para Saldo Inicial
const SaldoInicialModal = ({
  onClose,
  onSuccess,
  caixas,
  saldosIniciais = [],
  sendRequest,
  startLoading,
  stopLoading,
  auth,
  clinicaId,
}) => {
  const [formData, setFormData] = useState({
    caixaId: "",
    saldoInicial: "",
    dataReferencia: new Date().toISOString().split("T")[0],
    observacoes: "",
  });
  const [error, setError] = useState("");

  const saldoExistente = saldosIniciais.find(
    (s) => s.caixa._id === formData.caixaId,
  );
  const modoEdicao = !!saldoExistente;

  const handleSubmit = async () => {
    setError("");

    const hoje = new Date();
    hoje.setHours(23, 59, 59, 999);
    const dataEscolhida = new Date(formData.dataReferencia);

    if (dataEscolhida > hoje) {
      setError(
        "Data inválida. Saldo Inicial só pode ser registrado para hoje ou datas anteriores.",
      );
      return;
    }

    try {
      startLoading();

      const formDataToSend = new FormData();
      formDataToSend.append("saldoInicial", formData.saldoInicial);
      formDataToSend.append("dataReferencia", formData.dataReferencia);
      formDataToSend.append("observacoes", formData.observacoes || "");
      formDataToSend.append("utilizador", auth.userId);

      if (modoEdicao) {
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/saldosiniciais/${saldoExistente._id}`,
          "PATCH",
          formDataToSend,
          { Authorization: "Bearer " + auth.token },
        );
      } else {
        formDataToSend.append("clinicaId", clinicaId);
        formDataToSend.append("caixaId", formData.caixaId);
        formDataToSend.append("criadoPor", auth.userId);
        await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/saldosiniciais/`,
          "POST",
          formDataToSend,
          { Authorization: "Bearer " + auth.token },
        );
      }
      onSuccess();
    } catch (err) {
      setError(
        modoEdicao
          ? "Erro ao atualizar saldo inicial"
          : "Erro ao definir saldo inicial",
      );
    } finally {
      stopLoading();
    }
  };

  return (
    <div className="depositoCaixa-modal-overlay">
      <div className="depositoCaixa-modal">
        <div className="depositoCaixa-modal-header">
          <div className="depositoCaixa-modal-header-content">
            <h2 className="depositoCaixa-modal-title">
              Definir Saldo Inicial (Início do dia)
            </h2>
            <button onClick={onClose} className="depositoCaixa-modal-close">
              <X className="depositoCaixa-icon" />
            </button>
          </div>
        </div>

        <div className="depositoCaixa-modal-content">
          {error && (
            <div className="depositoCaixa-alert depositoCaixa-alert-error">
              <AlertTriangle className="depositoCaixa-icon" />
              <span className="depositoCaixa-alert-text">{error}</span>
            </div>
          )}

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">Caixa</label>
            <select
              value={formData.caixaId}
              onChange={(e) => {
                const caixaId = e.target.value;
                const existente = saldosIniciais.find(
                  (s) => s.caixa._id === caixaId,
                );
                setFormData({
                  caixaId,
                  saldoInicial: existente ? existente.saldoInicial : "",
                  dataReferencia: existente
                    ? existente.dataReferencia.split("T")[0]
                    : new Date().toISOString().split("T")[0],
                  observacoes: existente ? existente.observacoes || "" : "",
                });
              }}
              className="depositoCaixa-select"
              required
            >
              <option value="">Selecione...</option>
              {caixas.map((caixa) => (
                <option key={caixa._id} value={caixa._id}>
                  {caixa.caixa}
                </option>
              ))}
            </select>
            <p className="depositoCaixa-help-text">
              Recomenda-se definir apenas para a "Clínica" (dinheiro físico)
            </p>
          </div>

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">Saldo Inicial</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.saldoInicial}
              onChange={(e) =>
                setFormData({ ...formData, saldoInicial: e.target.value })
              }
              className="depositoCaixa-input"
              placeholder="0.00"
              required
            />
          </div>

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">Data de Referência</label>
            <DatePickerPT
              value={formData.dataReferencia}
              onChange={(value) =>
                setFormData({ ...formData, dataReferencia: value })
              }
              maxDate={new Date().toISOString().split("T")[0]}
              placeholder="Selecione a data de referência"
              required
            />
            <p className="depositoCaixa-help-text">
              Movimentações anteriores a esta data não serão consideradas
            </p>
          </div>

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">Observações</label>
            <textarea
              value={formData.observacoes}
              onChange={(e) =>
                setFormData({ ...formData, observacoes: e.target.value })
              }
              className="depositoCaixa-textarea"
              rows="3"
              placeholder="Observações opcionais..."
            />
          </div>

          <div className="depositoCaixa-modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="depositoCaixa-btn depositoCaixa-btn-outline"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="depositoCaixa-btn depositoCaixa-btn-yellow"
            >
              {modoEdicao ? "Atualizar Saldo" : "Definir Saldo"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositosDashboard;
